import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { ICellRendererParams } from 'ag-grid-community';
import cloneDeep from 'lodash.clonedeep';
import { map, Observable, startWith } from 'rxjs';
import { nameOf } from '../../../../../../core/functions/string-helpers';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { FormInputControlErrorKeys } from '../../../../../../shared/components/grid/cell-renderers/form-input-cell-renderer/constants/form-input-control-error-keys';
import { AntennaValidationMessages } from '../../constants/antenna-validation-messages';
import { ScheduleDValidationMessages } from '../../constants/schedule-d-validation-messages';
import { AntennaFormModel } from '../../models/antenna-form-model';
import { FrequencyFormModel } from '../../models/frequency-form-model';
import { ScheduleDFormModel } from '../../models/schedule-d-form-model';
import { SchedulesDAndHFormModel } from '../../models/schedules-d-and-h-form-model';
import { ScheduleDFormService } from '../schedule-d-form/schedule-d-form.service';

@Injectable({
    providedIn: 'root',
})
export class SchedulesDAndHFormService {
    private readonly FIXED_LOCATION_MAXIMUM = 6;

    constructor(private readonly scheduleDFormService: ScheduleDFormService) {}

    getFormDefinition(): FormGroup<
        NestedFormGroupType<SchedulesDAndHFormModel>
    > {
        return new FormGroup<NestedFormGroupType<SchedulesDAndHFormModel>>({
            scheduleDs: new FormArray<
                FormGroup<FormGroupType<ScheduleDFormModel>>
            >([], [Validators.required, this.fixedLocationAmountValidator()]),
            antennas: new FormArray<FormGroup<FormGroupType<AntennaFormModel>>>(
                [],
                {
                    validators: Validators.required,
                },
            ),
            frequencies: new FormArray<
                FormGroup<FormGroupType<FrequencyFormModel>>
            >([], {
                validators: Validators.required,
            }),
        });
    }

    getFixedLocations(
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): FormGroup<FormGroupType<ScheduleDFormModel>>[] {
        return scheduleDs.controls.filter(
            (location) =>
                this.scheduleDFormService.isFixed(location) &&
                location.controls.isDeleted.value !== true,
        ) as FormGroup<FormGroupType<ScheduleDFormModel>>[];
    }

    atMaximumFixedLocations(
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): boolean {
        return (
            this.getFixedLocations(scheduleDs).length >=
            this.FIXED_LOCATION_MAXIMUM
        );
    }

    /**
     * Validates the number of fixed locations on an application.
     * @returns a validator function that checks if the number of fixed locations is less than or equal to the maximum number of fixed locations allowed on an application.
     */
    fixedLocationAmountValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const scheduleDsControl = control as FormArray<
                FormGroup<FormGroupType<ScheduleDFormModel>>
            >;

            const fixedLocations = this.getFixedLocations(scheduleDsControl);

            if (fixedLocations.length > this.FIXED_LOCATION_MAXIMUM) {
                return {
                    [nameOf<typeof ScheduleDValidationMessages>(
                        'exceededMaximumFixedLocations',
                    )]:
                        ScheduleDValidationMessages.exceededMaximumFixedLocations,
                };
            }

            return null;
        };
    }

    /**
     * Gets the available Schedule D numbers for the antenna grid dropdown.
     * @returns an observable of dropdown options for Schedule D numbers.
     */
    getScheduleDNumberDropdownOptions(
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): Observable<DropdownOption[]> {
        return scheduleDs.valueChanges.pipe(
            startWith(cloneDeep((scheduleDs as FormArray).value)),
            map((scheduleDs) => {
                return scheduleDs.map((scheduleD: ScheduleDFormModel) => ({
                    value: scheduleD.number,
                    label: scheduleD.number?.toString(),
                }));
            }),
        );
    }

    /**
     * Gets the available Schedule D numbers for the location grid dropdown.
     * Only gets location numbers for locations that have antennas.
     * @returns an observable of dropdown options for Schedule D numbers for locations.
     */
    getAntennaLocationNumberDropdownOptions(
        antennas: FormArray<FormGroup<FormGroupType<AntennaFormModel>>>,
    ): Observable<DropdownOption[]> {
        return antennas.valueChanges.pipe(
            startWith(cloneDeep((antennas as FormArray).value)),
            map((antennas) => {
                const options = antennas
                    .filter(
                        (antenna: AntennaFormModel) =>
                            antenna.locationNumber !== null,
                    )
                    .sort(
                        (a: AntennaFormModel, b: AntennaFormModel) =>
                            a.locationNumber! - b.locationNumber!,
                    )
                    .map((antenna: AntennaFormModel) => ({
                        value: antenna.locationNumber,
                        label: antenna.locationNumber?.toString(),
                    }));

                return this.removeDuplicateDropdownOptions(options);
            }),
        );
    }

    /**
     * Gets the available Schedule D numbers for the antenna grid dropdown.
     * @returns an observable of dropdown options for Schedule D numbers.
     */
    getAntennaNumberDropdownOptions(
        antennas: FormArray<FormGroup<FormGroupType<AntennaFormModel>>>,
        params: ICellRendererParams | undefined,
    ): Observable<DropdownOption[]> {
        return antennas.valueChanges.pipe(
            startWith(cloneDeep((antennas as FormArray).value)),
            map((antennas) => {
                const locationNumber = params?.data.locationNumber;

                const options = antennas
                    .filter(
                        (antenna: AntennaFormModel) =>
                            antenna.locationNumber === locationNumber,
                    )
                    .map((antenna: AntennaFormModel) => ({
                        value: antenna.number,
                        label: antenna.number?.toString(),
                    }));

                return this.removeDuplicateDropdownOptions(options);
            }),
        );
    }

    /**
     * Finds the Schedule D form group that corresponds with the given antenna form.
     * @param antenna
     * @returns the matching Schedule D form group.
     */
    getAntennaLocation(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): FormGroup<FormGroupType<ScheduleDFormModel>> {
        const locationNumber = antenna.controls.locationNumber.value;

        return this.getLocationByNumber(locationNumber!, scheduleDs);
    }

    /**
     * Finds the Schedule D form group that corresponds with the given frequency form.
     * @param frequency
     * @returns the matching Schedule D form group.
     */
    getFrequencyLocation(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): FormGroup<FormGroupType<ScheduleDFormModel>> {
        const locationNumber = frequency.controls.locationNumber.value;

        return this.getLocationByNumber(locationNumber!, scheduleDs);
    }

    /**
     * Finds the Antenna form group that corresponds with the given frequency form.
     * @param frequency
     * @returns the matching Antenna form group.
     */
    getFrequencyAntenna(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
        antennas: FormArray<FormGroup<FormGroupType<AntennaFormModel>>>,
    ): FormGroup<FormGroupType<AntennaFormModel>> {
        const locationNumber = frequency.controls.locationNumber.value;
        const antennaNumber = frequency.controls.antennaNumber.value;

        return antennas.controls.find(
            (antenna) =>
                antenna.value.number === antennaNumber &&
                antenna.value.locationNumber === locationNumber,
        )!;
    }

    /**
     * Gets the antennas that correspond with the given location.
     * @param scheduleDForm the location form group
     * @param antennas all of the antennas in the form
     * @returns the antennas that correspond with the given location
     */
    getAntennasForLocation(
        scheduleDForm: FormGroup<FormGroupType<ScheduleDFormModel>>,
        antennas: FormArray<FormGroup<FormGroupType<AntennaFormModel>>>,
    ): FormGroup<FormGroupType<AntennaFormModel>>[] {
        return this.getAntennaIndexesForLocation(scheduleDForm, antennas).map(
            (index) => antennas.at(index),
        );
    }

    /**
     * Gets the frequencies that correspond with the given location.
     * @param scheduleDForm the location form group
     * @param frequencies all of the frequencies in the form
     * @returns the frequencies that correspond with the given location
     */
    getFrequenciesForLocation(
        scheduleDForm: FormGroup<FormGroupType<ScheduleDFormModel>>,
        frequencies: FormArray<
            FormGroup<NestedFormGroupType<FrequencyFormModel>>
        >,
    ): FormGroup<NestedFormGroupType<FrequencyFormModel>>[] {
        return this.getFrequencyIndexesForLocation(
            scheduleDForm,
            frequencies,
        ).map((index) => frequencies.at(index));
    }

    /**
     * Gets the frequencies that correspond with the given location.
     * @param scheduleDForm the location form group
     * @param frequencies all of the frequencies in the form
     * @returns the frequencies that correspond with the given location
     */
    getFrequenciesForAntenna(
        antennaForm: FormGroup<FormGroupType<AntennaFormModel>>,
        frequencies: FormArray<
            FormGroup<NestedFormGroupType<FrequencyFormModel>>
        >,
    ): FormGroup<NestedFormGroupType<FrequencyFormModel>>[] {
        return this.getFrequencyIndexesForAntenna(antennaForm, frequencies).map(
            (index) => frequencies.at(index),
        );
    }

    /**
     * Validates the antenna height to tip against the location's height with appurtenances.
     *
     * @returns a validator function that checks if the antenna height to tip is less than or equal to the location's height with appurtenances.
     */
    antennaHeightToTipValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const scheduleDFormArray = control.parent?.parent
                ?.parent as FormGroup<
                NestedFormGroupType<SchedulesDAndHFormModel>
            >;
            const antennaForm = control.parent as FormGroup<
                FormGroupType<AntennaFormModel>
            >;

            if (!scheduleDFormArray || !antennaForm) {
                return null;
            }

            const location = antennaForm.controls.locationNumber.value
                ? this.getAntennaLocation(
                      antennaForm,
                      scheduleDFormArray.controls.scheduleDs as FormArray<
                          FormGroup<FormGroupType<ScheduleDFormModel>>
                      >,
                  )
                : null;

            if (!location) {
                return null;
            }

            // Convert string to number, value comes as string from form control instead of numeric
            const heightToTip = Number(control!.value);

            const maximumHeight = Number(
                location.controls.overallHeightOfStructure.value,
            );

            if (maximumHeight && heightToTip && maximumHeight < heightToTip) {
                return {
                    [FormInputControlErrorKeys.grid]:
                        AntennaValidationMessages.heightGreaterThanScheduleDHeight,
                };
            }

            return null;
        };
    }

    private getLocationByNumber(
        locationNumber: number,
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): FormGroup<FormGroupType<ScheduleDFormModel>> {
        return scheduleDs.controls.find(
            (location) => location.value.number === locationNumber,
        )!;
    }

    /**
     * Gets the indexes of the antennas that correspond with the given location.
     * @param scheduleDForm the location form group
     * @param antennas all of the antennas in the form
     * @returns the indexes of the antennas that correspond with the given location
     */
    private getAntennaIndexesForLocation(
        scheduleDForm: FormGroup<FormGroupType<ScheduleDFormModel>>,
        antennas: FormArray<FormGroup<FormGroupType<AntennaFormModel>>>,
    ): number[] {
        const result: number[] = [];
        antennas.getRawValue().forEach((antenna, index) => {
            if (antenna.locationNumber === scheduleDForm.value.number) {
                result.push(index);
            }
        });

        return result;
    }

    /**
     * Gets the indexes of the frequencies that correspond with the given location.
     * @param scheduleDForm the location form group
     * @param frequencies all of the frequencies in the form
     * @returns the indexes of the frequencies that correspond with the given location
     */
    private getFrequencyIndexesForLocation(
        scheduleDForm: FormGroup<FormGroupType<ScheduleDFormModel>>,
        frequencies: FormArray<
            FormGroup<NestedFormGroupType<FrequencyFormModel>>
        >,
    ): number[] {
        const result: number[] = [];
        frequencies.getRawValue().forEach((frequency, index) => {
            if (frequency.locationNumber === scheduleDForm.value.number) {
                result.push(index);
            }
        });

        return result;
    }

    /**
     * Gets the indexes of the frequencies that correspond with the given antenna.
     * @param antennaForm the antenna form group
     * @param frequencies all of the frequencies in the form
     * @returns the indexes of the frequencies that correspond with the given antenna
     */
    private getFrequencyIndexesForAntenna(
        antennaForm: FormGroup<FormGroupType<AntennaFormModel>>,
        frequencies: FormArray<
            FormGroup<NestedFormGroupType<FrequencyFormModel>>
        >,
    ): number[] {
        const result: number[] = [];
        frequencies.getRawValue().forEach((frequency, index) => {
            if (
                frequency.antennaNumber === antennaForm.value.number &&
                frequency.locationNumber === antennaForm.value.locationNumber
            ) {
                result.push(index);
            }
        });

        return result;
    }

    private removeDuplicateDropdownOptions(options: DropdownOption[]) {
        return options.filter(
            (option: DropdownOption, index: number) =>
                options.findIndex(
                    (innerOption: DropdownOption) =>
                        innerOption.value === option.value,
                ) === index,
        );
    }
}
